import { Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, AlertTriangle, Users, FileText, Map, Scale, Building, HelpCircle, ExternalLink } from "lucide-react";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import Breadcrumb from "../components/common/Breadcrumb";

const RemembrementForestierPage = () => {
  // SEO: Update document title and meta on mount
  useEffect(() => {
    document.title = "Remembrement forestier : regroupement de parcelles | Sylva Invest";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Comprendre le remembrement forestier, le morcellement des parcelles et les solutions de regroupement foncier pour recomposer une propriété forestière.');
    }
    
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://sylva-invest.com/remembrement-forestier');
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', 'Remembrement forestier : regroupement de parcelles | Sylva Invest');
    
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', 'Comprendre le remembrement forestier, le morcellement des parcelles et les solutions de regroupement foncier pour recomposer une propriété forestière.');
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', 'https://sylva-invest.com/remembrement-forestier');
    
    return () => {
      document.title = "Sylva Invest — Transactions Forestières, Remembrement & Conseil (France)";
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Sylva Invest accompagne propriétaires, héritiers et investisseurs pour recomposer le foncier forestier morcelé. Transactions forestières réglementées (Carte T), remembrement et conseil stratégique en France.');
      }
      if (canonical) {
        canonical.setAttribute('href', 'https://sylva-invest.com/');
      }
    };
  }, []);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Remembrement forestier : regroupement de parcelles",
    "description": "Comprendre le remembrement forestier, le morcellement des parcelles et les solutions de regroupement foncier pour recomposer une propriété forestière.",
    "author": {
      "@type": "Organization",
      "name": "Sylva Invest",
      "url": "https://sylva-invest.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Sylva Invest",
      "url": "https://sylva-invest.com"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://sylva-invest.com/remembrement-forestier"
    }
  };

  // Schema.org FAQPage structured data (7 questions)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Qu'est-ce que le remembrement forestier ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Le terme « remembrement forestier » désigne généralement les opérations visant à regrouper des parcelles forestières dispersées afin de constituer des unités foncières plus cohérentes. Il peut s'agir de procédures administratives encadrées par l'État (aménagement foncier) ou, plus couramment, de démarches amiables reposant sur des acquisitions, ventes ou échanges entre propriétaires."
        }
      },
      {
        "@type": "Question",
        "name": "Pourquoi regrouper des parcelles forestières ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Le regroupement parcellaire permet de constituer des propriétés plus faciles à exploiter, mieux desservies, plus simples à transmettre et potentiellement plus valorisables. Une forêt regroupée offre généralement de meilleures conditions d'exploitation et de gestion qu'un ensemble de parcelles dispersées."
        }
      },
      {
        "@type": "Question",
        "name": "Comment acheter une parcelle de bois voisine ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "L'acquisition amiable d'une parcelle voisine suppose d'identifier le propriétaire (via le cadastre, les services de publicité foncière ou un notaire), d'établir un contact, de négocier les conditions de la vente, puis de formaliser la transaction par un acte authentique chez un notaire."
        }
      },
      {
        "@type": "Question",
        "name": "Comment retrouver le propriétaire d'une parcelle forestière ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Le plan cadastral (consultable sur cadastre.gouv.fr) permet d'identifier les références d'une parcelle. Pour connaître l'identité du propriétaire, il est possible de consulter les services de publicité foncière, de s'adresser à la mairie ou de solliciter un notaire qui dispose d'outils de recherche adaptés."
        }
      },
      {
        "@type": "Question",
        "name": "Peut-on échanger des parcelles forestières ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Oui, l'échange de parcelles entre propriétaires est une solution courante pour améliorer la cohérence géographique des propriétés respectives. L'échange doit être formalisé par un acte notarié. Si les parcelles ne sont pas de valeur équivalente, une soulte peut être versée pour compenser la différence."
        }
      },
      {
        "@type": "Question",
        "name": "Quel est le rôle du cadastre dans un projet de regroupement parcellaire ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Le cadastre est un document fiscal qui permet de visualiser la configuration des parcelles, d'identifier leurs références et de connaître leur surface officielle. Il constitue un point de départ utile, mais ne fait pas foi en matière de limites de propriété ni de droits réels. Pour des informations juridiques précises, il convient de consulter les titres de propriété et les services de publicité foncière."
        }
      },
      {
        "@type": "Question",
        "name": "Comment Sylva Invest accompagne-t-il une recomposition foncière forestière ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sylva Invest accompagne les propriétaires dans leurs projets de regroupement foncier : analyse cadastrale, identification et prise de contact avec les propriétaires voisins, négociation, et intermédiation réglementée (Carte T) pour sécuriser les transactions d'achat, de vente ou d'échange de parcelles forestières."
        }
      }
    ]
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      
      <Navigation />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-forest text-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Breadcrumb items={[
              { label: "Ressources", href: "/ressources" },
              { label: "Remembrement Forestier" }
            ]} />
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-sans font-semibold mb-4">
                Guide Pratique
              </p>
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-6" data-testid="remembrement-h1">
                Remembrement Forestier : Comprendre et Agir
              </h1>
              <p className="font-sans text-base md:text-lg text-white/80 leading-relaxed">
                Morcellement forestier, regroupement parcellaire, recomposition foncière : 
                les clés pour comprendre et agir sur votre patrimoine forestier.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Table of Contents */}
        <section className="py-8 bg-bone border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <nav aria-label="Sommaire" className="font-sans text-sm">
              <p className="font-semibold text-forest mb-3">Sommaire</p>
              <ul className="flex flex-wrap gap-x-6 gap-y-2 text-gray-600">
                <li><a href="#morcellement" className="hover:text-gold transition-colors">Morcellement forestier</a></li>
                <li><a href="#remembrement" className="hover:text-gold transition-colors">Remembrement et regroupement</a></li>
                <li><a href="#consequences" className="hover:text-gold transition-colors">Conséquences du morcellement</a></li>
                <li><a href="#methodes" className="hover:text-gold transition-colors">Méthodes de regroupement</a></li>
                <li><a href="#cadastre" className="hover:text-gold transition-colors">Rôle du cadastre</a></li>
                <li><a href="#successions" className="hover:text-gold transition-colors">Successions et indivisions</a></li>
                <li><a href="#transactions" className="hover:text-gold transition-colors">Transactions forestières</a></li>
                <li><a href="#accompagnement" className="hover:text-gold transition-colors">Accompagnement Sylva Invest</a></li>
                <li><a href="#faq" className="hover:text-gold transition-colors">FAQ</a></li>
              </ul>
            </nav>
          </div>
        </section>

        {/* Main Content */}
        <article className="py-16 md:py-20 bg-cream">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            
            {/* Section: Morcellement Forestier */}
            <section id="morcellement" className="mb-16 scroll-mt-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-serif text-2xl md:text-3xl text-forest mb-6">
                  Le Morcellement Forestier en France
                </h2>
                <div className="prose prose-lg max-w-none font-sans text-gray-700 leading-relaxed space-y-4">
                  <p>
                    Le <strong className="text-forest">morcellement forestier</strong> désigne la fragmentation 
                    d'une propriété forestière en de multiples parcelles de petite taille, souvent dispersées 
                    géographiquement et appartenant à différents propriétaires ou à une même famille sur 
                    plusieurs générations.
                  </p>
                  <p>
                    La forêt privée française est caractérisée par un morcellement important. Selon les 
                    données du ministère de l'Agriculture, la forêt privée représente environ 75 % de la 
                    surface forestière française et compte plusieurs millions de propriétaires, dont une 
                    grande partie détient des surfaces inférieures à 4 hectares.
                  </p>
                  
                  <h3 className="font-serif text-xl text-forest mt-8 mb-4">
                    Les facteurs du morcellement forestier
                  </h3>
                  <p>
                    Plusieurs facteurs, historiques et juridiques, contribuent à expliquer cette situation :
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span><strong>Les transmissions successorales :</strong> Au fil des générations, 
                      les partages entre héritiers peuvent conduire à la division progressive des 
                      propriétés forestières.</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span><strong>Le régime successoral français :</strong> Le droit civil français 
                      prévoit des règles de partage entre héritiers qui, appliquées à chaque génération, 
                      peuvent contribuer à fragmenter le patrimoine foncier.</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span><strong>Les ventes partielles historiques :</strong> Au fil du temps, des portions 
                      de forêt ont pu être vendues séparément pour diverses raisons économiques ou familiales.</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span><strong>L'absence de politique de regroupement :</strong> Contrairement au 
                      foncier agricole, le foncier forestier a moins bénéficié de politiques 
                      d'aménagement foncier structurées.</span>
                    </li>
                  </ul>
                </div>
              </motion.div>
            </section>

            {/* Section: Définition du Remembrement */}
            <section id="remembrement" className="mb-16 scroll-mt-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-serif text-2xl md:text-3xl text-forest mb-6">
                  Remembrement Forestier et Regroupement Foncier
                </h2>
                <div className="prose prose-lg max-w-none font-sans text-gray-700 leading-relaxed space-y-4">
                  <p>
                    Le terme <strong className="text-forest">« remembrement forestier »</strong> est 
                    couramment utilisé pour désigner les opérations visant à regrouper des parcelles 
                    forestières dispersées afin de constituer des unités foncières plus cohérentes.
                  </p>
                  <p>
                    Ce terme peut toutefois recouvrir des réalités différentes :
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span><strong>L'aménagement foncier forestier (AFAF) :</strong> procédure 
                      administrative encadrée par l'État, permettant de restructurer le parcellaire 
                      forestier sur un périmètre défini.</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span><strong>Le regroupement foncier amiable :</strong> démarches volontaires 
                      entre propriétaires pour acquérir, vendre ou échanger des parcelles et 
                      reconstituer des ensembles cohérents.</span>
                    </li>
                  </ul>
                  
                  <div className="bg-bone p-6 mt-6 border-l-4 border-gold">
                    <p className="text-forest font-medium mb-2">Terminologie</p>
                    <p className="text-gray-600 text-sm">
                      Dans le cadre de l'accompagnement proposé par Sylva Invest, nous privilégions 
                      les expressions <strong>« recomposition foncière forestière »</strong>, 
                      <strong>« regroupement parcellaire »</strong> ou <strong>« regroupement 
                      foncier forestier »</strong> pour désigner les démarches amiables 
                      d'acquisition, de vente ou d'échange entre propriétaires. Sylva Invest 
                      n'intervient pas dans le cadre de procédures administratives d'aménagement foncier.
                    </p>
                  </div>

                  <p className="mt-6">
                    L'objectif du regroupement est de constituer des propriétés forestières :
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span>Plus faciles à exploiter et à entretenir</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span>Mieux desservies par des accès praticables</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span>Plus simples à transmettre aux générations futures</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span>Potentiellement plus valorisables</span>
                    </li>
                  </ul>
                </div>
              </motion.div>
            </section>

            {/* Section: Conséquences du Morcellement */}
            <section id="consequences" className="mb-16 scroll-mt-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-serif text-2xl md:text-3xl text-forest mb-6">
                  Les Conséquences du Morcellement pour les Propriétaires
                </h2>
                <div className="prose prose-lg max-w-none font-sans text-gray-700 leading-relaxed space-y-4">
                  <p>
                    Le morcellement forestier peut engendrer plusieurs difficultés pratiques 
                    pour les propriétaires :
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div className="bg-white p-6 border-l-4 border-gold">
                      <AlertTriangle className="w-6 h-6 text-gold mb-3" />
                      <h4 className="font-serif text-lg text-forest mb-2">Difficultés d'accès</h4>
                      <p className="text-sm text-gray-600">
                        Des parcelles enclavées ou mal desservies peuvent être difficiles 
                        à exploiter, voire simplement à visiter.
                      </p>
                    </div>
                    <div className="bg-white p-6 border-l-4 border-gold">
                      <AlertTriangle className="w-6 h-6 text-gold mb-3" />
                      <h4 className="font-serif text-lg text-forest mb-2">Coûts disproportionnés</h4>
                      <p className="text-sm text-gray-600">
                        Les frais d'exploitation et les charges administratives peuvent 
                        devenir importants rapportés à la surface de chaque petite parcelle.
                      </p>
                    </div>
                    <div className="bg-white p-6 border-l-4 border-gold">
                      <AlertTriangle className="w-6 h-6 text-gold mb-3" />
                      <h4 className="font-serif text-lg text-forest mb-2">Complexité de transmission</h4>
                      <p className="text-sm text-gray-600">
                        Un patrimoine morcelé peut être plus complexe à transmettre 
                        et moins attractif pour les générations suivantes.
                      </p>
                    </div>
                    <div className="bg-white p-6 border-l-4 border-gold">
                      <AlertTriangle className="w-6 h-6 text-gold mb-3" />
                      <h4 className="font-serif text-lg text-forest mb-2">Valorisation limitée</h4>
                      <p className="text-sm text-gray-600">
                        La faible superficie, l'enclavement, l'accessibilité et la configuration 
                        d'une parcelle peuvent influencer sa valeur et sa liquidité sur le marché.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </section>

            {/* Section: Méthodes de Regroupement */}
            <section id="methodes" className="mb-16 scroll-mt-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-serif text-2xl md:text-3xl text-forest mb-6">
                  Les Méthodes de Regroupement Parcellaire
                </h2>
                <div className="prose prose-lg max-w-none font-sans text-gray-700 leading-relaxed space-y-4">
                  <p>
                    Plusieurs méthodes permettent de regrouper progressivement des parcelles 
                    forestières et de reconstituer des unités foncières cohérentes :
                  </p>
                  
                  <h3 className="font-serif text-xl text-forest mt-8 mb-4 flex items-center">
                    <Building className="w-6 h-6 text-gold mr-3" />
                    L'Acquisition Amiable de Parcelles Voisines
                  </h3>
                  <p>
                    La méthode la plus directe consiste à acquérir des parcelles contiguës ou 
                    proches de sa propriété existante. Cette approche suppose :
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li>• D'identifier les propriétaires des parcelles voisines</li>
                    <li>• D'établir un contact et de proposer une acquisition</li>
                    <li>• De négocier les conditions de la vente</li>
                    <li>• De formaliser la transaction par un acte authentique chez un notaire</li>
                  </ul>

                  <h3 className="font-serif text-xl text-forest mt-8 mb-4 flex items-center">
                    <Scale className="w-6 h-6 text-gold mr-3" />
                    Les Échanges de Parcelles
                  </h3>
                  <p>
                    L'échange permet à deux propriétaires de s'échanger des parcelles pour 
                    améliorer la cohérence géographique de leurs propriétés respectives. 
                    Cette solution peut présenter l'avantage de limiter les besoins en 
                    trésorerie lorsque les parcelles échangées sont de valeur équivalente. 
                    Si tel n'est pas le cas, une soulte peut être versée pour compenser 
                    la différence. L'échange doit être formalisé par un acte notarié.
                  </p>

                  <h3 className="font-serif text-xl text-forest mt-8 mb-4 flex items-center">
                    <Users className="w-6 h-6 text-gold mr-3" />
                    L'Identification et le Contact des Propriétaires Voisins
                  </h3>
                  <p>
                    Un travail préalable d'identification des propriétaires est souvent nécessaire. 
                    Il peut s'appuyer sur :
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li>• La consultation du plan cadastral (références des parcelles)</li>
                    <li>• Les demandes auprès des services de publicité foncière (identification des propriétaires)</li>
                    <li>• Le contact avec les notaires locaux</li>
                    <li>• Les échanges avec les acteurs locaux (mairies, voisins, professionnels forestiers)</li>
                  </ul>
                  
                  <p className="mt-4">
                    Pour en savoir plus sur les <Link to="/#services" className="text-gold hover:underline">services d'accompagnement</Link> proposés 
                    par Sylva Invest dans ce domaine.
                  </p>
                </div>
              </motion.div>
            </section>

            {/* Section: Rôle du Cadastre */}
            <section id="cadastre" className="mb-16 scroll-mt-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-serif text-2xl md:text-3xl text-forest mb-6 flex items-center">
                  <Map className="w-8 h-8 text-gold mr-3" />
                  Le Rôle du Cadastre
                </h2>
                <div className="prose prose-lg max-w-none font-sans text-gray-700 leading-relaxed space-y-4">
                  <p>
                    Le <strong className="text-forest">cadastre</strong> est avant tout un document 
                    à vocation fiscale, qui recense les propriétés foncières et sert de base 
                    au calcul des impôts locaux. Le plan cadastral permet de :
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span>Visualiser la configuration générale des parcelles</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span>Identifier les références cadastrales (section, numéro de parcelle)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span>Connaître la surface cadastrale de chaque parcelle</span>
                    </li>
                  </ul>
                  
                  <div className="bg-bone p-6 mt-6 border-l-4 border-gold">
                    <p className="text-forest font-medium mb-2">Important</p>
                    <p className="text-gray-600 text-sm">
                      Le plan cadastral ne constitue pas, à lui seul, une preuve des limites 
                      de propriété ni des droits et servitudes. Pour établir les limites 
                      exactes d'une parcelle, un bornage par un géomètre-expert peut être 
                      nécessaire. Pour connaître les servitudes et droits réels attachés 
                      à un bien, il convient de consulter les titres de propriété et les 
                      services de publicité foncière.
                    </p>
                  </div>
                  
                  <p className="mt-6">
                    Le plan cadastral est consultable gratuitement sur le site 
                    officiel <a href="https://www.cadastre.gouv.fr" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">cadastre.gouv.fr</a>. 
                    Pour identifier le propriétaire d'une parcelle, il est généralement 
                    nécessaire de s'adresser aux services de publicité foncière ou à un notaire.
                  </p>
                </div>
              </motion.div>
            </section>

            {/* Section: Successions et Indivisions */}
            <section id="successions" className="mb-16 scroll-mt-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-serif text-2xl md:text-3xl text-forest mb-6 flex items-center">
                  <FileText className="w-8 h-8 text-gold mr-3" />
                  Successions et Indivisions
                </h2>
                <div className="prose prose-lg max-w-none font-sans text-gray-700 leading-relaxed space-y-4">
                  <p>
                    Les <strong className="text-forest">successions</strong> constituent l'un des 
                    facteurs importants du morcellement forestier. À chaque génération, les 
                    partages entre héritiers peuvent fragmenter un peu plus les propriétés.
                  </p>
                  <p>
                    L'<strong className="text-forest">indivision</strong> — situation où plusieurs 
                    personnes sont propriétaires ensemble d'un même bien sans que leurs parts 
                    respectives soient matériellement divisées — peut également compliquer 
                    la gestion du patrimoine forestier :
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <AlertTriangle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span>Les décisions importantes concernant le bien nécessitent généralement 
                      l'accord des indivisaires, selon les règles prévues par le Code civil</span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span>Des désaccords entre indivisaires peuvent bloquer certaines décisions</span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span>La situation peut se complexifier avec le temps et les générations successives</span>
                    </li>
                  </ul>
                  <p>
                    Sortir d'une indivision ou organiser un partage entre héritiers nécessite 
                    généralement l'intervention d'un notaire. Cela peut impliquer des transactions 
                    entre indivisaires (rachat de parts) ou avec des tiers.
                  </p>
                  <p>
                    Pour plus d'informations sur les règles applicables en matière de succession 
                    et d'indivision, le site <a href="https://www.service-public.fr/particuliers/vosdroits/N171" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Service-Public.fr</a> propose 
                    des fiches pratiques régulièrement mises à jour.
                  </p>
                </div>
              </motion.div>
            </section>

            {/* Section: Transactions Forestières */}
            <section id="transactions" className="mb-16 scroll-mt-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-serif text-2xl md:text-3xl text-forest mb-6">
                  Les Transactions Forestières
                </h2>
                <div className="prose prose-lg max-w-none font-sans text-gray-700 leading-relaxed space-y-4">
                  <p>
                    Les <strong className="text-forest">transactions forestières</strong> regroupent 
                    l'ensemble des opérations d'achat, de vente et d'échange de parcelles forestières.
                  </p>
                  <p>
                    En France, ces transactions immobilières peuvent être réalisées :
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span><strong>Directement entre particuliers :</strong> le vendeur et l'acheteur 
                      conviennent des conditions de la vente, puis font appel à un notaire pour 
                      établir l'acte authentique de vente.</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                      <span><strong>Par l'intermédiaire d'un professionnel de l'immobilier :</strong> 
                      les agents immobiliers titulaires d'une carte professionnelle (Carte T, 
                      délivrée par la CCI) peuvent assurer l'intermédiation et accompagner 
                      les parties dans la transaction.</span>
                    </li>
                  </ul>
                  <p>
                    L'intervention d'un professionnel peut être particulièrement utile lorsque :
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li>• Les propriétaires voisins sont difficiles à identifier ou à contacter</li>
                    <li>• La négociation nécessite un accompagnement (contexte familial, conditions particulières)</li>
                    <li>• La transaction s'inscrit dans un projet de regroupement plus large</li>
                    <li>• Des vérifications préalables approfondies sont souhaitées</li>
                  </ul>
                  
                  <p className="mt-4">
                    Découvrez les <Link to="/#services" className="text-gold hover:underline">services de transaction forestière</Link> proposés 
                    par Sylva Invest.
                  </p>
                </div>
              </motion.div>
            </section>

            {/* Section: Accompagnement Sylva Invest */}
            <section id="accompagnement" className="mb-16 scroll-mt-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="bg-forest text-white p-8 md:p-10">
                  <h2 className="font-serif text-2xl md:text-3xl mb-6">
                    Comment Sylva Invest Peut Vous Accompagner
                  </h2>
                  <div className="font-sans text-white/90 leading-relaxed space-y-4">
                    <p>
                      Sylva Invest accompagne les propriétaires forestiers, les héritiers et les 
                      investisseurs dans leurs projets de <strong className="text-gold">recomposition 
                      foncière forestière</strong>.
                    </p>
                    <p>
                      Notre accompagnement peut porter sur :
                    </p>
                    <ul className="space-y-3 mt-4">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                        <span><strong className="text-gold">L'analyse foncière et cadastrale :</strong> identification 
                        des parcelles, repérage des propriétaires voisins, analyse des opportunités de regroupement.</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                        <span><strong className="text-gold">L'identification et la prise de contact :</strong> recherche 
                        et approche des propriétaires voisins avec discrétion et professionnalisme.</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                        <span><strong className="text-gold">La négociation :</strong> accompagnement 
                        dans les discussions et recherche de solutions équilibrées.</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                        <span><strong className="text-gold">Les transactions forestières :</strong> intermédiation 
                        dans le cadre de l'activité réglementée (Carte T) pour sécuriser les opérations 
                        d'achat, de vente ou d'échange.</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" />
                        <span><strong className="text-gold">Le conseil stratégique :</strong> aide à la définition 
                        d'une feuille de route pour reconstituer progressivement une propriété cohérente.</span>
                      </li>
                    </ul>
                    
                    <div className="mt-8 pt-6 border-t border-white/20">
                      <p className="text-white/80 mb-4">
                        Vous avez un projet de regroupement foncier ou des parcelles forestières à valoriser ?
                      </p>
                      <Link 
                        to="/#contact"
                        className="btn-primary bg-gold text-forest hover:bg-white hover:text-forest inline-block"
                        data-testid="cta-contact"
                      >
                        Discutons de Votre Projet
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            </section>

            {/* Section: FAQ */}
            <section id="faq" className="mb-16 scroll-mt-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-serif text-2xl md:text-3xl text-forest mb-8 flex items-center">
                  <HelpCircle className="w-8 h-8 text-gold mr-3" />
                  Questions Fréquentes
                </h2>
                
                <div className="space-y-6">
                  {/* FAQ Item 1 */}
                  <div className="bg-white p-6 border-l-4 border-gold">
                    <h3 className="font-serif text-lg text-forest mb-3">
                      Qu'est-ce que le remembrement forestier ?
                    </h3>
                    <p className="font-sans text-gray-600 text-sm leading-relaxed">
                      Le terme « remembrement forestier » désigne généralement les opérations visant à 
                      regrouper des parcelles forestières dispersées afin de constituer des unités 
                      foncières plus cohérentes. Il peut s'agir de procédures administratives encadrées 
                      par l'État (aménagement foncier) ou, plus couramment, de démarches amiables 
                      reposant sur des acquisitions, ventes ou échanges entre propriétaires.
                    </p>
                  </div>

                  {/* FAQ Item 2 */}
                  <div className="bg-white p-6 border-l-4 border-gold">
                    <h3 className="font-serif text-lg text-forest mb-3">
                      Pourquoi regrouper des parcelles forestières ?
                    </h3>
                    <p className="font-sans text-gray-600 text-sm leading-relaxed">
                      Le regroupement parcellaire permet de constituer des propriétés plus faciles à 
                      exploiter, mieux desservies, plus simples à transmettre et potentiellement plus 
                      valorisables. Une forêt regroupée offre généralement de meilleures conditions 
                      d'exploitation qu'un ensemble de parcelles dispersées.
                    </p>
                  </div>

                  {/* FAQ Item 3 */}
                  <div className="bg-white p-6 border-l-4 border-gold">
                    <h3 className="font-serif text-lg text-forest mb-3">
                      Comment acheter une parcelle de bois voisine ?
                    </h3>
                    <p className="font-sans text-gray-600 text-sm leading-relaxed">
                      L'acquisition amiable d'une parcelle voisine suppose d'identifier le propriétaire 
                      (via le cadastre, les services de publicité foncière ou un notaire), d'établir un 
                      contact, de négocier les conditions de la vente, puis de formaliser la transaction 
                      par un acte authentique chez un notaire.
                    </p>
                  </div>

                  {/* FAQ Item 4 */}
                  <div className="bg-white p-6 border-l-4 border-gold">
                    <h3 className="font-serif text-lg text-forest mb-3">
                      Comment retrouver le propriétaire d'une parcelle forestière ?
                    </h3>
                    <p className="font-sans text-gray-600 text-sm leading-relaxed">
                      Le plan cadastral (consultable sur <a href="https://www.cadastre.gouv.fr" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">cadastre.gouv.fr</a>) permet 
                      d'identifier les références d'une parcelle. Pour connaître l'identité du propriétaire, 
                      il est possible de consulter les services de publicité foncière, de s'adresser à la 
                      mairie ou de solliciter un notaire qui dispose d'outils de recherche adaptés.
                    </p>
                  </div>

                  {/* FAQ Item 5 */}
                  <div className="bg-white p-6 border-l-4 border-gold">
                    <h3 className="font-serif text-lg text-forest mb-3">
                      Peut-on échanger des parcelles forestières ?
                    </h3>
                    <p className="font-sans text-gray-600 text-sm leading-relaxed">
                      Oui, l'échange de parcelles entre propriétaires est une solution courante pour 
                      améliorer la cohérence géographique des propriétés respectives. L'échange doit 
                      être formalisé par un acte notarié. Si les parcelles ne sont pas de valeur 
                      équivalente, une soulte peut être versée pour compenser la différence.
                    </p>
                  </div>

                  {/* FAQ Item 6 */}
                  <div className="bg-white p-6 border-l-4 border-gold">
                    <h3 className="font-serif text-lg text-forest mb-3">
                      Quel est le rôle du cadastre dans un projet de regroupement parcellaire ?
                    </h3>
                    <p className="font-sans text-gray-600 text-sm leading-relaxed">
                      Le cadastre est un document fiscal qui permet de visualiser la configuration des 
                      parcelles, d'identifier leurs références et de connaître leur surface officielle. 
                      Il constitue un point de départ utile, mais ne fait pas foi en matière de limites 
                      de propriété ni de droits réels. Pour des informations juridiques précises, il 
                      convient de consulter les titres de propriété et les services de publicité foncière.
                    </p>
                  </div>

                  {/* FAQ Item 7 */}
                  <div className="bg-white p-6 border-l-4 border-gold">
                    <h3 className="font-serif text-lg text-forest mb-3">
                      Comment Sylva Invest accompagne-t-il une recomposition foncière forestière ?
                    </h3>
                    <p className="font-sans text-gray-600 text-sm leading-relaxed">
                      Sylva Invest accompagne les propriétaires dans leurs projets de regroupement 
                      foncier : analyse cadastrale, identification et prise de contact avec les 
                      propriétaires voisins, négociation, et intermédiation réglementée (Carte T) 
                      pour sécuriser les transactions d'achat, de vente ou d'échange de parcelles 
                      forestières. <Link to="/#contact" className="text-gold hover:underline">Contactez-nous</Link> pour 
                      discuter de votre projet.
                    </p>
                  </div>
                </div>
              </motion.div>
            </section>

            {/* Section: Sources */}
            <section id="sources" className="mb-8 scroll-mt-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="border-t border-gray-200 pt-8">
                  <h2 className="font-serif text-xl text-forest mb-4">
                    Sources et Ressources Utiles
                  </h2>
                  <p className="font-sans text-sm text-gray-600 mb-4">
                    Pour approfondir les sujets abordés dans cette page, vous pouvez consulter 
                    les ressources institutionnelles suivantes :
                  </p>
                  <ul className="space-y-2 font-sans text-sm">
                    <li className="flex items-start">
                      <ExternalLink className="w-4 h-4 text-gold mr-2 mt-0.5 flex-shrink-0" />
                      <a 
                        href="https://www.cnpf.fr" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-forest hover:text-gold transition-colors"
                      >
                        Centre National de la Propriété Forestière (CNPF)
                      </a>
                      <span className="text-gray-500 ml-1">— Information et conseil aux propriétaires forestiers</span>
                    </li>
                    <li className="flex items-start">
                      <ExternalLink className="w-4 h-4 text-gold mr-2 mt-0.5 flex-shrink-0" />
                      <a 
                        href="https://agriculture.gouv.fr/la-foret-et-le-bois-en-france" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-forest hover:text-gold transition-colors"
                      >
                        Ministère de l'Agriculture et de la Souveraineté alimentaire
                      </a>
                      <span className="text-gray-500 ml-1">— Politiques forestières et données statistiques</span>
                    </li>
                    <li className="flex items-start">
                      <ExternalLink className="w-4 h-4 text-gold mr-2 mt-0.5 flex-shrink-0" />
                      <a 
                        href="https://www.cadastre.gouv.fr" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-forest hover:text-gold transition-colors"
                      >
                        cadastre.gouv.fr
                      </a>
                      <span className="text-gray-500 ml-1">— Consultation du plan cadastral</span>
                    </li>
                    <li className="flex items-start">
                      <ExternalLink className="w-4 h-4 text-gold mr-2 mt-0.5 flex-shrink-0" />
                      <a 
                        href="https://www.service-public.fr/particuliers/vosdroits/N171" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-forest hover:text-gold transition-colors"
                      >
                        Service-Public.fr — Succession
                      </a>
                      <span className="text-gray-500 ml-1">— Fiches pratiques sur les successions et l'indivision</span>
                    </li>
                    <li className="flex items-start">
                      <ExternalLink className="w-4 h-4 text-gold mr-2 mt-0.5 flex-shrink-0" />
                      <a 
                        href="https://www.notaires.fr" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-forest hover:text-gold transition-colors"
                      >
                        Notaires de France
                      </a>
                      <span className="text-gray-500 ml-1">— Transactions immobilières et actes notariés</span>
                    </li>
                    <li className="flex items-start">
                      <ExternalLink className="w-4 h-4 text-gold mr-2 mt-0.5 flex-shrink-0" />
                      <a 
                        href="https://www.legifrance.gouv.fr" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-forest hover:text-gold transition-colors"
                      >
                        Légifrance
                      </a>
                      <span className="text-gray-500 ml-1">— Textes législatifs et réglementaires</span>
                    </li>
                    <li className="flex items-start">
                      <ExternalLink className="w-4 h-4 text-gold mr-2 mt-0.5 flex-shrink-0" />
                      <a 
                        href="https://www.ign.fr" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-forest hover:text-gold transition-colors"
                      >
                        Institut national de l'information géographique et forestière (IGN)
                      </a>
                      <span className="text-gray-500 ml-1">— Données géographiques et forestières</span>
                    </li>
                  </ul>
                </div>
              </motion.div>
            </section>
          </div>
        </article>

        {/* Related Resources */}
        <section className="py-16 bg-bone">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-serif text-2xl md:text-3xl text-forest mb-8 text-center">
              Continuer Votre Lecture
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/ressources"
                className="bg-white px-6 py-3 text-forest hover:text-gold transition-colors font-sans text-sm"
              >
                ← Toutes les Ressources
              </Link>
              <Link 
                to="/#services"
                className="bg-forest text-white px-6 py-3 hover:bg-gold hover:text-forest transition-colors font-sans text-sm"
              >
                Nos Services
              </Link>
              <Link 
                to="/#contact"
                className="bg-gold text-forest px-6 py-3 hover:bg-forest hover:text-white transition-colors font-sans text-sm"
              >
                Nous Contacter
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default RemembrementForestierPage;
